import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiCreditCard, FiEye, FiInfo, FiSearch, FiSend, FiUpload, FiX, FiXCircle } from 'react-icons/fi';
import useAPI from '../../hooks/useAPI';
import usePutTransaksi from '../../hooks/useUpdateTransaksi';
import useKonfirmasiTransaksiCRUD from '../../hooks/useKonfirmasiTransaksiCRUD';
import endpointsAdmin from '../../Services/endpointAdmin';
import { addNotification } from '../../Services/notifikasi';
import api, { buildFileUrl, resolveApiBaseUrl } from '../../Services/api';
import {
  applyDistributionToCategoryRows,
  calculateDistributedByCategory,
  resolveTransactionFundingType,
} from '../../Services/donationBalance';
import {
  buildAdminMitraUserScope,
  extractArrayPayload,
  findFieldDeep,
  getAdminLembagaScope,
  matchItemToAdminScope,
  normalizeScopeValue,
} from './adminScope';
import { openDonationReceiptPdf } from './exportUtilsAdmin';

function KonfirmasiDonasiAdmin({ admin }) {
  const { data: transaksiData, loading, error, refetch } = useAPI('/api/transaksi');
  const { data: jenisDonasiData } = useAPI(endpointsAdmin.jenis_donasi.getAll);
  const { data: detailDonasiData } = useAPI(endpointsAdmin.detail_donasi.getAll);
  const { data: konfirmasiData, refetch: refetchKonfirmasi } = useAPI(endpointsAdmin.konfirmasi_transaksi.getAll);
  const { data: penyaluranData } = useAPI(endpointsAdmin.penyaluran_program.getAll);
  const { data: programData } = useAPI(endpointsAdmin.program.getAll);
  const { putTransaksi, loading: updating, error: updateError } = usePutTransaksi();
  const {
    postKonfirmasiTransaksi,
    putKonfirmasiTransaksi,
    loading: savingKonfirmasi,
    error: konfirmasiError,
  } = useKonfirmasiTransaksiCRUD();
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);
  const [loadingAdminMitra, setLoadingAdminMitra] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [sendingReceiptId, setSendingReceiptId] = useState(null);
  const [confirmationFile, setConfirmationFile] = useState(null);
  const [rejectCandidate, setRejectCandidate] = useState(null);
  const adminKodeUser = String(findFieldDeep(admin, ['kode_user', 'kodeUser']) || '').trim();
  const adminDisplayName = findFieldDeep(admin, ['nama_user', 'nama', 'name', 'username']) || 'Admin';
  const lembagaScope = getAdminLembagaScope(admin);

  const transaksiList = useMemo(() => {
    if (Array.isArray(transaksiData)) return transaksiData;
    if (Array.isArray(transaksiData?.transaksi)) return transaksiData.transaksi;
    if (Array.isArray(transaksiData?.data)) return transaksiData.data;
    if (Array.isArray(transaksiData?.result)) return transaksiData.result;
    return [];
  }, [transaksiData]);

  const jenisDonasiList = useMemo(() => {
    if (Array.isArray(jenisDonasiData)) return jenisDonasiData;
    if (Array.isArray(jenisDonasiData?.jenis_donasi)) return jenisDonasiData.jenis_donasi;
    if (Array.isArray(jenisDonasiData?.data)) return jenisDonasiData.data;
    if (Array.isArray(jenisDonasiData?.result)) return jenisDonasiData.result;
    return [];
  }, [jenisDonasiData]);

  const detailDonasiList = useMemo(
    () => extractArrayPayload(detailDonasiData, ['detail_donasi']),
    [detailDonasiData]
  );
  const konfirmasiList = useMemo(
    () => extractArrayPayload(konfirmasiData, ['konfirmasi_transaksi', 'data_konfirmasi_transaksi', 'konfirmasi']),
    [konfirmasiData]
  );
  const penyaluranList = useMemo(
    () => extractArrayPayload(penyaluranData, ['penyaluran_program']),
    [penyaluranData]
  );
  const programList = useMemo(
    () => extractArrayPayload(programData, ['program']),
    [programData]
  );

  const formatCurrency = (value) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(Number(value || 0) || 0);

  const formatDateTime = (value) => {
    if (!value) return '-';

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return String(value);

    return parsedDate.toLocaleString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getJenisMeta = useCallback((trx) => {
    const kodeJenis =
      trx?.kode_jenis_donasi ||
      trx?.kodeJenisDonasi ||
      trx?.jenis_donasi?.kode_jenis_donasi ||
      trx?.jenisDonasi?.kode_jenis_donasi ||
      trx?.jenis_donasi?.kode ||
      trx?.jenisDonasi?.kode ||
      trx?.kode_jenis ||
      '';

    const matchedJenis = jenisDonasiList.find((item) => {
      const itemKode =
        item?.kode_jenis_donasi ||
        item?.kodeJenisDonasi ||
        item?.kode ||
        item?.kode_jenis ||
        '';

      return normalizeScopeValue(itemKode) === normalizeScopeValue(kodeJenis);
    });

    const namaJenis =
      trx.jenisDonasi?.nama_donasi ||
      trx.jenis_donasi?.nama_donasi ||
      matchedJenis?.nama_donasi ||
      matchedJenis?.namaDonasi ||
      trx.jenisDonasi?.nama ||
      trx.jenis_donasi?.nama ||
      matchedJenis?.nama ||
      matchedJenis?.nama_jenis ||
      trx.nama_jenis_donasi ||
      'Donasi Lainnya';

    return {
      kode: kodeJenis,
      nama: namaJenis,
    };
  }, [jenisDonasiList]);

  useEffect(() => {
    let cancelled = false;

    const fetchAdminMitra = async () => {
      if (!lembagaScope.active) {
        setAdminMitraRecords([]);
        return;
      }

      try {
        setLoadingAdminMitra(true);
        const response = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        setAdminMitraRecords(extractArrayPayload(response.data, ['adminmitra', 'data_adminmitra', 'users']));
      } catch (err) {
        if (cancelled) return;
        setAdminMitraRecords([]);
      } finally {
        if (!cancelled) {
          setLoadingAdminMitra(false);
        }
      }
    };

    fetchAdminMitra();

    return () => {
      cancelled = true;
    };
  }, [lembagaScope.active, lembagaScope.id]);

  const adminMitraScope = useMemo(
    () => buildAdminMitraUserScope(adminMitraRecords),
    [adminMitraRecords]
  );

  const getDonaturDisplay = (trx) => {
    const donatur = trx?.donatur || trx?.data_donatur || trx?.donatur_detail;

    if (typeof trx?.nama_donatur === 'string' && trx.nama_donatur.trim()) {
      return trx.nama_donatur;
    }

    if (donatur && typeof donatur === 'object') {
      return donatur.nama || donatur.nama_donatur || donatur.kode_donatur || donatur.email || '-';
    }

    if (typeof trx?.kode_donatur === 'string' && trx.kode_donatur.trim()) {
      return trx.kode_donatur;
    }

    if (typeof trx?.kode_donatur === 'object' && trx.kode_donatur !== null) {
      return trx.kode_donatur.nama || trx.kode_donatur.kode_donatur || '-';
    }

    return '-';
  };

  const getDonaturEntity = (trx) => trx?.donatur || trx?.data_donatur || trx?.donatur_detail || null;

  const getDonaturCodeDisplay = (trx) => {
    const donatur = getDonaturEntity(trx);

    if (typeof trx?.kode_donatur === 'string' && trx.kode_donatur.trim()) {
      return trx.kode_donatur;
    }

    if (donatur && typeof donatur === 'object') {
      return donatur.kode_donatur || donatur.kodeDonatur || donatur.kode || '-';
    }

    if (typeof trx?.kode_donatur === 'object' && trx.kode_donatur !== null) {
      return trx.kode_donatur.kode_donatur || trx.kode_donatur.kode || '-';
    }

    return '-';
  };

  const getDonaturEmailDisplay = (trx) => {
    const donatur = getDonaturEntity(trx);
    return donatur?.email || trx?.email_donatur || '-';
  };

  const getDonaturPhoneDisplay = (trx) => {
    const donatur = getDonaturEntity(trx);
    return donatur?.no_hp || donatur?.nomor_hp || donatur?.phone || trx?.no_hp || '-';
  };

  const getDetailDonasiDisplay = (trx) =>
    trx.detailDonasi?.nama_detail_donasi ||
    trx.detail_donasi?.nama_detail_donasi ||
    trx.detailDonasi?.nama ||
    trx.detail_donasi?.nama ||
    trx.nama_detail_donasi ||
    '-';

  const getExplicitProofImageValue = (trx) => {
    if (!trx || typeof trx !== 'object') return '';
    return (
      trx?.foto_bukti ||
      trx?.foto_bukti_transfer ||
      trx?.bukti_transfer ||
      trx?.bukti_pembayaran ||
      trx?.bukti ||
      trx?.bukti_pembayaran_url ||
      ''
    );
  };

  const getProofImageValue = (trx) => {
    const explicit = getExplicitProofImageValue(trx);
    return explicit && typeof explicit === 'string' ? explicit.trim() : '';
  };

  const getProofImageUrl = (trx) => {
    const value = getProofImageValue(trx);
    if (!value) return '';

    const trimmed = value.trim();
    if (trimmed.startsWith('data:image/')) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    // Try several fallbacks for files uploaded to the server (relative paths)
    // Prefer frontend origin first for local uploads, then API-origin based URLs.
    const hostPath = trimmed.replace(/^\/+/, '');
    const tryWindow = (typeof window !== 'undefined') ? `${window.location.origin.replace(/\/+$/g, '')}/${hostPath}` : '';
    const tryBuild = buildFileUrl(trimmed);
    const apiBase = String(resolveApiBaseUrl() || '').replace(/\/+$/g, '');
    const tryApiBase = apiBase ? `${apiBase.replace(/\/$/, '')}/${hostPath}` : '';

    // When developing locally, the frontend origin often cannot serve uploaded files.
    // Prefer the API/file-server URL when running on localhost.
    if (typeof window !== 'undefined' && window.location && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
      return tryBuild || tryApiBase || tryWindow;
    }

    return tryWindow || tryBuild || tryApiBase;
  };

  const getTransactionDbId = (trx) =>
    trx?.id_transaksi ||
    trx?.id ||
    trx?.kode_transaksi ||
    trx?.kode ||
    null;

  const getKonfirmasiId = (konfirmasi) =>
    konfirmasi?.id_konfirmasi_transaksi ||
    konfirmasi?.idKonfirmasiTransaksi ||
    konfirmasi?.id_konfirmasi ||
    konfirmasi?.id ||
    null;

  const findKonfirmasiForTransaction = (trx) => {
    if (!Array.isArray(konfirmasiList) || konfirmasiList.length === 0) return null;

    const transactionIds = [trx?.id_transaksi, trx?.id, trx?.kode_transaksi, trx?.kode].map((v) => (v === undefined || v === null ? '' : String(v)));
    const kurbanIds = [trx?.id_transaksi_kurban, trx?.transaksi_kurban?.id, trx?.transaksiKurban?.id].map((v) => (v === undefined || v === null ? '' : String(v)));

    return konfirmasiList.find((item) => {
      const idTrans = item?.id_transaksi || item?.transaksi?.id || item?.transaksi_donasi?.id || '';
      const idTransKurban = item?.id_transaksi_kurban || item?.transaksi_kurban?.id || '';

      return (
        (idTrans && transactionIds.includes(String(idTrans))) ||
        (idTransKurban && kurbanIds.includes(String(idTransKurban)))
      );
    }) || null;
  };

  const getProofUrlForTransaction = (trx) => {
    const konf = findKonfirmasiForTransaction(trx);
    const directExplicit = getExplicitProofImageValue(trx);
    const konfExplicit = getExplicitProofImageValue(konf);

    const directUrl = directExplicit ? getProofImageUrl({ foto_bukti: directExplicit }) : '';
    const konfUrl = konfExplicit ? getProofImageUrl({ foto_bukti: konfExplicit }) : '';

    return directUrl || konfUrl;
  };

  const getMetodeDisplay = (trx) => {
    if (trx.himpun?.nama_himpun) return trx.himpun.nama_himpun;
    if (trx.himpun?.nama) return trx.himpun.nama;
    if (trx.detailTransaksi?.nama) return trx.detailTransaksi.nama;
    if (trx.detail_transaksi?.nama) return trx.detail_transaksi.nama;
    return trx.metode_pembayaran || trx.metode || trx.jalur_pembayaran || trx.nama_detail_transaksi || '-';
  };

  const getDetailHimpunDisplay = (trx) => {
    if (trx.detail_himpun?.nama) return trx.detail_himpun.nama;
    if (trx.detailHimpun?.nama) return trx.detailHimpun.nama;
    if (trx.kode_detail_transaksi) return trx.kode_detail_transaksi;
    return trx.detail_transaksi?.kode_detail_transaksi || trx.detailTransaksi?.kode_detail_transaksi || '-';
  };

  const getStatusDisplay = (trx) => {
    const status = (
      trx.status ||
      trx.status_transaksi ||
      trx.statusTransaksi ||
      'pending'
    )
      .toString()
      .toLowerCase();

    if (status === 'success' || status === 'verified' || status === 'diverifikasi' || status === 'berhasil') {
      return 'Sukses';
    }

    if (status === 'diproses' || status === 'processed' || status === 'processing') {
      return 'Diproses';
    }

    if (status === 'ditolak' || status === 'rejected' || status === 'reject' || status === 'tolak') {
      return 'Ditolak';
    }

    return 'Pending';
  };

  const getStatusBadgeClass = (statusLabel) => {
    if (statusLabel === 'Sukses') {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }

    if (statusLabel === 'Diproses') {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }

    if (statusLabel === 'Ditolak') {
      return 'bg-red-50 text-red-700 border-red-200';
    }

    return 'bg-white text-slate-600 border-slate-200';
  };

  const buildReceiptPayload = (trx) => {
    const receiptCode = trx?.kode_transaksi || trx?.kode || trx?.id || '-';
    const donaturName = getDonaturDisplay(trx);
    const amount = formatCurrency(trx?.jumlah || trx?.jumlah_donasi || 0);
    const transactionDate = formatDateTime(
      trx?.tanggal_transaksi || trx?.tgl_transaksi || trx?.created_at || trx?.tanggal || trx?.tgl || null
    );
    const confirmationDate = formatDateTime(
      trx?.updated_at || trx?.tanggal_konfirmasi || trx?.tgl_konfirmasi || trx?.updatedAt || trx?.created_at || null
    );
    const confirmerName =
      trx?.nama_user ||
      trx?.user?.nama_user ||
      trx?.user?.nama ||
      adminDisplayName ||
      '-';
    const confirmerCode =
      trx?.kode_user ||
      trx?.user?.kode_user ||
      trx?.user?.kode ||
      adminKodeUser ||
      '-';
    const confirmerRole = trx?.role || trx?.user?.role || 'Admin';

    return {
      title: `Struk Donasi ${receiptCode}`,
      receiptCode,
      generatedAt: formatDateTime(new Date().toISOString()),
      summary: `${donaturName} telah melakukan transaksi donasi sebesar ${amount}. Struk ini diterbitkan setelah pembayaran dikonfirmasi oleh petugas/admin.`,
      sections: [
        {
          title: 'Data Donatur',
          items: [
            { label: 'Nama Donatur', value: donaturName },
            { label: 'Kode Donatur', value: getDonaturCodeDisplay(trx) },
            { label: 'Email', value: getDonaturEmailDisplay(trx) },
            { label: 'No. Handphone', value: getDonaturPhoneDisplay(trx) },
          ],
        },
        {
          title: 'Data Transaksi',
          items: [
            { label: 'Kode Transaksi', value: receiptCode },
            { label: 'Jenis Donasi', value: getJenisMeta(trx).nama },
            { label: 'Program Donasi', value: getDetailDonasiDisplay(trx) },
            { label: 'Nominal', value: amount },
            { label: 'Tanggal Transaksi', value: transactionDate },
            { label: 'Metode Pembayaran', value: getMetodeDisplay(trx) },
            { label: 'Detail Metode', value: getDetailHimpunDisplay(trx) },
            { label: 'Status', value: getStatusDisplay(trx) },
            { label: 'Catatan', value: trx?.catatan || trx?.catatan_donatur || '-' },
          ],
        },
        {
          title: 'Konfirmasi Transaksi',
          items: [
            { label: 'Status Konfirmasi', value: getStatusDisplay(trx) },
            { label: 'Tanggal Konfirmasi', value: confirmationDate },
            { label: 'Kode User Pengonfirmasi', value: confirmerCode },
            { label: 'Nama Pengonfirmasi', value: confirmerName },
          ],
        },
        {
          title: 'Petugas / Admin',
          items: [
            { label: 'Nama Petugas', value: confirmerName },
            { label: 'Kode User', value: confirmerCode },
            { label: 'Role', value: confirmerRole },
            { label: 'Lembaga', value: lembagaScope.name || '-' },
          ],
        },
      ],
      footerNote: 'Gunakan menu print browser untuk menyimpan sebagai PDF atau mengirimkan struk ini ke donatur.',
    };
  };

  const normalizeWhatsAppPhone = (value) => {
    const digits = String(value || '').replace(/\D/g, '');

    if (!digits) return '';
    if (digits.startsWith('62')) return digits;
    if (digits.startsWith('0')) return `62${digits.slice(1)}`;
    return digits;
  };

  const handleSendReceipt = async (trx) => {
    setActionMessage('');

    const rowId = trx?.id || trx?.kode_transaksi || trx?.kode;
    setSendingReceiptId(rowId);
    const rawPhone = getDonaturPhoneDisplay(trx);
    const normalizedPhone = normalizeWhatsAppPhone(rawPhone);

    if (!normalizedPhone || normalizedPhone.length < 10) {
      setActionMessage('Nomor WhatsApp donatur tidak valid atau belum tersedia.');
      setSendingReceiptId(null);
      return;
    }

    const receiptPayload = buildReceiptPayload(trx);
    const donaturName = getDonaturDisplay(trx);
    const receiptCode = receiptPayload.receiptCode;

    let message = `*STRUK BUKTI DONASI*\n\n`;
    message += `Assalamu'alaikum ${donaturName},\n`;
    message += `Terima kasih atas donasi Anda. Berikut adalah rincian donasi Anda:\n\n`;

    receiptPayload.sections.forEach(section => {
      message += `*-- ${section.title.toUpperCase()} --*\n`;
      section.items.forEach(item => {
        if (item.value && item.value !== '-') {
          message += `${item.label}: ${item.value}\n`;
        }
      });
      message += `\n`;
    });

    message += `Semoga Allah membalas kebaikan Anda dengan pahala yang berlipat ganda. Aamiin.`;

    const encodedMessage = encodeURIComponent(message);
    const waLink = `https://wa.me/${normalizedPhone}?text=${encodedMessage}`;

    window.open(waLink, '_blank');
    setActionMessage(`Membuka WhatsApp untuk mengirim struk teks transaksi ${receiptCode}.`);
    setSendingReceiptId(null);
  };

  const handlePreviewReceipt = (trx) => {
    const opened = openDonationReceiptPdf(buildReceiptPayload(trx));

    setActionMessage(
      opened
        ? `Preview struk transaksi ${trx?.kode_transaksi || trx?.id || '-'} berhasil dibuka.`
        : 'Popup browser diblokir. Izinkan popup untuk membuka struk PDF.'
    );
  };

  const filteredTransaksi = useMemo(() => (
    transaksiList.filter((trx) => matchItemToAdminScope(trx, lembagaScope, adminMitraScope))
  ), [transaksiList, lembagaScope, adminMitraScope]);

  const scopedPenyaluranList = useMemo(() => (
    penyaluranList.filter((item) => matchItemToAdminScope(item, lembagaScope, adminMitraScope))
  ), [penyaluranList, lembagaScope, adminMitraScope]);

  const distributedByCategory = useMemo(
    () => calculateDistributedByCategory(scopedPenyaluranList, programList),
    [scopedPenyaluranList, programList]
  );

  const laporan = useMemo(() => {
    let grossTotalNominal = 0;
    let totalTransaksi = 0;
    const kategoriMap = new Map();

    for (const trx of filteredTransaksi) {
      const nominal = Number(trx.jumlah || trx.jumlah_donasi || 0) || 0;
      const jenisMeta = getJenisMeta(trx);
      const kategoriNama = jenisMeta.nama;
      const sourceType = resolveTransactionFundingType(trx, detailDonasiList, kategoriNama);
      const key = normalizeScopeValue(jenisMeta.kode || kategoriNama || sourceType || 'lainnya');
      const current = kategoriMap.get(key) || {
        key,
        nama: kategoriNama,
        sourceType,
        total: 0,
      };

      grossTotalNominal += nominal;
      totalTransaksi += 1;
      current.total += nominal;
      if (!current.sourceType && sourceType) current.sourceType = sourceType;
      kategoriMap.set(key, current);
    }

    const kategoriRows = applyDistributionToCategoryRows(
      Array.from(kategoriMap.values()).sort((a, b) => b.total - a.total),
      distributedByCategory
    );
    const totalTersalur = Array.from(distributedByCategory.values()).reduce((sum, value) => sum + value, 0);
    const totalNominal = kategoriRows.reduce((sum, item) => sum + item.total, 0);

    return {
      totalNominal,
      grossTotalNominal,
      totalTersalur,
      totalTransaksi,
      kategori: kategoriRows.map((item) => [item.nama, item.grossTotal, item]),
    };
  }, [detailDonasiList, distributedByCategory, filteredTransaksi, getJenisMeta]);

  useEffect(() => {
    if (!filteredTransaksi.length) {
      setSelectedTransactionId(null);
      setIsDetailModalOpen(false);
      return;
    }

    const selectedStillExists = filteredTransaksi.some((trx) => String(trx.id || trx.kode_transaksi || trx.kode) === String(selectedTransactionId));

    if (!selectedStillExists) {
      const firstId = filteredTransaksi[0].id || filteredTransaksi[0].kode_transaksi || filteredTransaksi[0].kode;
      setSelectedTransactionId(firstId);
      if (isDetailModalOpen) {
        setIsDetailModalOpen(false);
      }
    }
  }, [filteredTransaksi, selectedTransactionId, isDetailModalOpen]);

  const selectedTransaction = useMemo(() => (
    filteredTransaksi.find((trx) => String(trx.id || trx.kode_transaksi || trx.kode) === String(selectedTransactionId)) || null
  ), [filteredTransaksi, selectedTransactionId]);

  const handleOpenDetail = (rowId) => {
    setSelectedTransactionId(rowId);
    setConfirmationFile(null);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setConfirmationFile(null);
  };

  const handleOpenRejectModal = (trx) => {
    setActionMessage('');
    setRejectCandidate(trx);
  };

  const handleCloseRejectModal = () => {
    if (updating || savingKonfirmasi) return;
    setRejectCandidate(null);
  };

  const handleConfirmReject = async () => {
    if (!rejectCandidate) return;
    const candidate = rejectCandidate;
    setRejectCandidate(null);
    await handleReject(candidate);
  };

  const handleConfirmationFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setConfirmationFile(file);
  };

  const handleConfirm = async (trx) => {
    setActionMessage('');
    const id = trx.id || trx.kode_transaksi || trx.kode;
    setActiveId(id);

    if (!id) {
      setActionMessage('Tidak dapat memperbarui transaksi: id transaksi tidak ditemukan.');
      setActiveId(null);
      return;
    }

    if (!adminKodeUser) {
      setActionMessage('Kode user admin yang sedang login tidak ditemukan. Lengkapi data admin terlebih dahulu sebelum verifikasi transaksi.');
      setActiveId(null);
      return;
    }

    if (!confirmationFile) {
      setActionMessage('Upload foto konfirmasi terlebih dahulu sebelum mengubah status transaksi menjadi sukses.');
      setActiveId(null);
      return;
    }

    try {
      const currentKonfirmasi = findKonfirmasiForTransaction(trx);
      const konfirmasiId = getKonfirmasiId(currentKonfirmasi);
      const konfirmasiPayload = {
        id_transaksi: getTransactionDbId(trx),
        id_transaksi_kurban: trx?.id_transaksi_kurban || null,
        foto_bukti: currentKonfirmasi?.foto_bukti || getExplicitProofImageValue(trx),
        foto_konfirmasi: confirmationFile,
        status: 'success',
      };

      if (konfirmasiId) {
        await putKonfirmasiTransaksi(konfirmasiId, konfirmasiPayload);
      } else {
        await postKonfirmasiTransaksi(konfirmasiPayload);
      }

      const response = await putTransaksi(
        id,
        trx.kode_transaksi,
        trx.kode_donatur,
        trx.kode_jenis_donasi,
        trx.kode_detail_donasi,
        adminKodeUser,
        trx.kode_himpun,
        trx.kode_detail_transaksi,
        trx.jumlah_donasi,
        'success'
      );

      if (response) {
        setConfirmationFile(null);
        setActionMessage('Status transaksi berhasil diubah menjadi sukses.');
        addNotification({
          title: 'Status Transaksi Dikonfirmasi',
          message: `Transaksi ${trx.kode_transaksi || id} telah dikonfirmasi ${adminDisplayName} dan status berubah menjadi sukses.`,
          userType: 'donatur',
          audienceKey:
            (typeof trx?.kode_donatur === 'string' && trx.kode_donatur) ||
            trx?.kode_donatur?.kode_donatur ||
            trx?.donatur?.kode_donatur ||
            trx?.data_donatur?.kode_donatur ||
            null,
        });
        await refetchKonfirmasi();
        await refetch();
      } else {
        setActionMessage('Foto konfirmasi tersimpan, tetapi status transaksi gagal diperbarui. Silakan coba lagi.');
      }
    } catch (e) {
      setActionMessage(e.message || 'Terjadi kesalahan saat mengonfirmasi transaksi.');
    } finally {
      setActiveId(null);
    }
  };

  const handleReject = async (trx) => {
    setActionMessage('');
    const id = trx.id || trx.kode_transaksi || trx.kode;
    setActiveId(id);

    if (!id) {
      setActionMessage('Tidak dapat menolak transaksi: id transaksi tidak ditemukan.');
      setActiveId(null);
      return;
    }

    if (!adminKodeUser) {
      setActionMessage('Kode user admin yang sedang login tidak ditemukan. Lengkapi data admin terlebih dahulu sebelum menolak transaksi.');
      setActiveId(null);
      return;
    }

    try {
      const currentKonfirmasi = findKonfirmasiForTransaction(trx);
      const konfirmasiId = getKonfirmasiId(currentKonfirmasi);

      if (konfirmasiId) {
        await putKonfirmasiTransaksi(konfirmasiId, {
          id_transaksi: getTransactionDbId(trx),
          id_transaksi_kurban: trx?.id_transaksi_kurban || null,
          foto_bukti: currentKonfirmasi?.foto_bukti || getExplicitProofImageValue(trx),
          foto_konfirmasi: currentKonfirmasi?.foto_konfirmasi || '',
          status: 'ditolak',
        });
      }

      const response = await putTransaksi(
        id,
        trx.kode_transaksi,
        trx.kode_donatur,
        trx.kode_jenis_donasi,
        trx.kode_detail_donasi,
        adminKodeUser,
        trx.kode_himpun,
        trx.kode_detail_transaksi,
        trx.jumlah_donasi,
        'ditolak'
      );

      if (response) {
        setConfirmationFile(null);
        setActionMessage('Status transaksi berhasil diubah menjadi ditolak.');
        addNotification({
          title: 'Transaksi Ditolak',
          message: `Transaksi ${trx.kode_transaksi || id} telah ditolak oleh ${adminDisplayName}.`,
          userType: 'donatur',
          audienceKey:
            (typeof trx?.kode_donatur === 'string' && trx.kode_donatur) ||
            trx?.kode_donatur?.kode_donatur ||
            trx?.donatur?.kode_donatur ||
            trx?.data_donatur?.kode_donatur ||
            null,
        });
        await refetchKonfirmasi();
        await refetch();
      } else {
        setActionMessage('Gagal mengubah status transaksi menjadi ditolak. Silakan coba lagi.');
      }
    } catch (e) {
      setActionMessage(e.message || 'Terjadi kesalahan saat menolak transaksi.');
    } finally {
      setActiveId(null);
    }
  };

  const pendingCount = filteredTransaksi.filter((trx) => {
    const status = (trx.status || trx.status_transaksi || trx.statusTransaksi || '').toString().toLowerCase();
    return status === 'pending';
  }).length;

  const readyToConfirmCount = filteredTransaksi.filter((trx) => {
    const status = (trx.status || trx.status_transaksi || trx.statusTransaksi || '').toString().toLowerCase();
    return status === 'diproses' || status === 'processed' || status === 'processing';
  }).length;

  const successCount = filteredTransaksi.filter((trx) => getStatusDisplay(trx) === 'Sukses').length;

  const metricCards = [
    {
      title: 'Total Donasi',
      value: formatCurrency(laporan.grossTotalNominal),
      note: 'Akumulasi nominal transaksi dalam scope admin.',
      icon: FiCreditCard,
      cardBg: 'bg-gradient-to-br from-blue-200 to-blue-1500',
      borderColor: 'border-blue-200',
      labelColor: 'text-blue-900',
      valueColor: 'text-black',
      noteColor: 'text-blue-900',
      iconBg: 'bg-blue-900 text-white',
    },
    {
      title: 'Total Transaksi',
      value: laporan.totalTransaksi,
      note: 'Semua data transaksi yang tampil di halaman ini.',
      icon: FiInfo,
      cardBg: 'bg-gradient-to-br from-indigo-200 to-indigo-100',
      borderColor: 'border-indigo-100',
      labelColor: 'text-indigo-600',
      valueColor: 'text-black',
      noteColor: 'text-indigo-900',
      iconBg: 'bg-indigo-900 text-white',
    },
    {
      title: 'Siap Dikonfirmasi',
      value: readyToConfirmCount,
      note: 'Transaksi berstatus diproses dan menunggu aksi admin.',
      icon: FiClock,
      cardBg: 'bg-gradient-to-br from-amber-200 to-orange-100',
      borderColor: 'border-amber-100',
      labelColor: 'text-amber-900',
      valueColor: 'text-black',
      noteColor: 'text-amber-900',
      iconBg: 'bg-amber-900 text-white',
    },
    {
      title: 'Sukses',
      value: successCount,
      note: 'Transaksi yang sudah berhasil diverifikasi.',
      icon: FiCheckCircle,
      cardBg: 'bg-gradient-to-br from-emerald-200 to-emerald-100',
      borderColor: 'border-emerald-100',
      labelColor: 'text-emerald-900',
      valueColor: 'text-black',
      noteColor: 'text-emerald-900',
      iconBg: 'bg-emerald-900 text-white',
    },
  ];

  const categoryTones = [
    'border-blue-200 bg-blue-50 text-blue-800',
    'border-emerald-200 bg-emerald-50 text-emerald-800',
    'border-amber-200 bg-amber-50 text-amber-800',
    'border-indigo-200 bg-indigo-50 text-indigo-800',
    'border-rose-200 bg-rose-50 text-rose-800',
    'border-cyan-200 bg-cyan-50 text-cyan-800',
  ];

  const DetailItem = ({ label, children }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 transition hover:bg-white hover:shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <div className="mt-2 text-sm font-semibold leading-6 text-slate-800">{children}</div>
    </div>
  );

  if (loading || loadingAdminMitra) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12 lg:px-16">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="relative mx-auto mb-5 w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
              <div className="animate-spin absolute inset-0 rounded-full border-4 border-blue-900 border-t-transparent"></div>
            </div>
            <p className="text-slate-700 font-semibold">Memuat data konfirmasi...</p>
            <p className="text-sm text-slate-400 mt-1">Mohon tunggu sebentar</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12 lg:px-16">
        <div className="flex items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <span className="text-red-500 font-bold text-lg">!</span>
          </div>
          <div>
            <p className="text-red-700 font-semibold">Terjadi Kesalahan</p>
            <p className="text-red-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="w-full space-y-7 px-4 py-8 sm:px-6 lg:px-10 xl:px-16">

        {/* ── HEADER ── */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />
          <div className="flex flex-col gap-5 px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/30 flex-shrink-0">
                <FiCheckCircle className="text-white text-xl" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-blue-700">Konfirmasi Transaksi</p>
                <h1 className="mt-0.5 text-2xl font-bold leading-tight text-slate-900 lg:text-3xl">Verifikasi Donasi Masuk</h1>
                <p className="mt-1 text-sm text-slate-500">
                  Pantau transaksi, cek rincian pembayaran, lalu konfirmasi data yang sudah siap diverifikasi.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-700 shadow-sm">
                <FiClock className="h-3.5 w-3.5" />
                {readyToConfirmCount} siap dikonfirmasi
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-bold text-slate-600 shadow-sm">
                <FiInfo className="h-3.5 w-3.5" />
                {pendingCount} pending
              </span>
            </div>
          </div>
        </section>

        {/* ── ACTION MESSAGE ── */}
        {actionMessage && (
          <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="w-5 h-5 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">i</span>
            </div>
            <p className="text-sm text-blue-800 font-medium">{actionMessage}</p>
          </div>
        )}

        {(updateError || konfirmasiError) && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-amber-700 text-xs font-bold">!</span>
            </div>
            <p className="text-sm text-amber-700 font-medium">{updateError || konfirmasiError}</p>
          </div>
        )}

        {/* ── METRIC CARDS ── */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metricCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.title}
                className={`group relative overflow-hidden rounded-2xl border ${card.borderColor} ${card.cardBg} p-5 shadow-md transition hover:shadow-xl hover:-translate-y-0.5 duration-200`}
              >
                {/* Subtle shine overlay on hover */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300 rounded-2xl" />

                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${card.labelColor}`}>{card.title}</p>
                    <p className={`mt-3 text-2xl font-extrabold ${card.valueColor}`}>{card.value}</p>
                  </div>
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${card.iconBg} shadow-sm flex-shrink-0`}>
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <p className={`relative mt-4 text-xs leading-5 ${card.noteColor}`}>{card.note}</p>
              </div>
            );
          })}
        </div>

        {/* ── KATEGORI ── */}
        <section className="overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-blue-100 bg-gradient-to-r from-blue-100 to-blue-150 px-6 py-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-blue-900">Rangkuman Kategori</p>
              <h2 className="mt-0.5 text-base font-bold text-Black">Donasi per Kategori</h2>
            </div>
            <span className="text-xs text-blue-900 font-medium">Ringkasan nominal transaksi aktif</span>
          </div>
          <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {laporan.kategori.length === 0 ? (
              <p className="col-span-full rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-400 text-center">Belum ada data kategori.</p>
            ) : (
              laporan.kategori.map(([nama, nominal], index) => {
                const tone = categoryTones[index % categoryTones.length];
                return (
                  <div key={nama} className={`rounded-xl border p-5 shadow-sm transition hover:shadow-md ${tone}`}>
                    <p className="text-sm font-bold">{nama}</p>
                    <p className="mt-3 text-xl font-extrabold">{formatCurrency(nominal)}</p>
                    <p className="mt-1 text-xs opacity-70">Total kotor kategori</p>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* ── TABEL TRANSAKSI ── */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 bg-gradient-to-r from-blue-100 to-blue-150 px-6 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-bold text-black">Daftar Transaksi Donasi</h2>
              <p className="mt-0.5 text-xs text-blue-900">Menampilkan {filteredTransaksi.length} transaksi sesuai akses admin.</p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-xl border border-blue-700 bg-blue-900 px-3 py-2 text-xs font-medium text-blue-200">
              <FiSearch className="h-3.5 w-3.5" />
              Pilih baris untuk melihat detail
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">No</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Kode</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Donatur</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Kategori</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Metode</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Kode Detail</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Tanggal</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Nominal</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-widest text-slate-500">Status</th>
                  <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-widest text-slate-500">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTransaksi.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-5 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
                          <FiCreditCard className="h-6 w-6 text-slate-300" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-700">Tidak ada transaksi yang tersedia.</p>
                          <p className="mt-1 text-sm text-slate-400">Data akan tampil setelah transaksi masuk sesuai scope admin.</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTransaksi.map((trx, idx) => {
                    const rowId = trx.id || trx.kode_transaksi || trx.kode;
                    const isActive = String(rowId) === String(selectedTransactionId);
                    const jenisMeta = getJenisMeta(trx);
                    const statusLabel = getStatusDisplay(trx);
                    const trxStatus = (trx.status || trx.status_transaksi || trx.statusTransaksi || '').toString().toLowerCase();
                    const canAdminConfirm = trxStatus === 'diproses' || trxStatus === 'processed' || trxStatus === 'processing';
                    const isSuccess = statusLabel === 'Sukses';

                    return (
                      <tr
                        key={rowId || idx}
                        onClick={() => handleOpenDetail(rowId)}
                        className={`cursor-pointer transition-colors duration-150 ${
                          isActive
                            ? 'bg-blue-50 border-l-4 border-l-blue-900'
                            : 'hover:bg-slate-50/80 border-l-4 border-l-transparent'
                        }`}
                      >
                        <td className="px-5 py-4 text-sm font-medium text-slate-400">{idx + 1}</td>
                        <td className="px-5 py-4">
                          <span className="text-sm font-bold text-slate-800 font-mono">{trx.kode_transaksi || '-'}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {(getDonaturDisplay(trx) || 'D').charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-semibold text-slate-800">{getDonaturDisplay(trx)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">{jenisMeta.nama}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{getMetodeDisplay(trx)}</td>
                        <td className="px-5 py-4 text-sm text-slate-500 font-mono">{getDetailHimpunDisplay(trx)}</td>
                        <td className="px-5 py-4 text-sm text-slate-500">
                          {formatDateTime(trx.tanggal_transaksi || trx.tgl_transaksi || trx.created_at || trx.tanggal || trx.tgl || null)}
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm font-bold text-slate-900">{formatCurrency(trx.jumlah || trx.jumlah_donasi || 0)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(statusLabel)}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                          {canAdminConfirm ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleOpenDetail(rowId)}
                                disabled={(updating || savingKonfirmasi) && activeId === rowId}
                                className="inline-flex min-w-[100px] items-center justify-center gap-1.5 rounded-xl bg-blue-900 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-blue-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <FiCheckCircle className="h-3.5 w-3.5" />
                                {(updating || savingKonfirmasi) && activeId === rowId ? 'Menyimpan...' : 'Konfirmasi'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenRejectModal(trx)}
                                disabled={(updating || savingKonfirmasi) && activeId === rowId}
                                className="inline-flex min-w-[72px] items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <FiXCircle className="h-3.5 w-3.5" />
                                Tolak
                              </button>
                            </div>
                          ) : trxStatus === 'pending' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-xs font-semibold text-amber-600">
                              <FiClock className="h-3 w-3" />
                              Menunggu donatur
                            </span>
                          ) : isSuccess ? (
                            <button
                              type="button"
                              onClick={() => handleSendReceipt(trx)}
                              disabled={sendingReceiptId === rowId}
                              className="inline-flex min-w-[100px] items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 active:scale-95"
                            >
                              <FiSend className="h-3.5 w-3.5" />
                              {sendingReceiptId === rowId ? 'Mengirim...' : 'Kirim Struk'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenDetail(rowId)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 active:scale-95"
                            >
                              <FiEye className="h-3.5 w-3.5" />
                              Detail
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

      </div>

      {/* ── MODAL DETAIL ── */}
      {selectedTransaction && isDetailModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
          onClick={handleCloseDetail}
        >
          <div
            className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 z-10 border-b border-slate-100 bg-white">
              <div className="h-1 w-full bg-gradient-to-r from-blue-900 to-blue-500" />
              <div className="flex items-start justify-between gap-4 px-6 py-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <FiCreditCard className="text-white h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Detail Transaksi</p>
                    <h2 className="mt-0.5 text-lg font-bold text-slate-900 font-mono">{selectedTransaction.kode_transaksi || '-'}</h2>
                    <p className="mt-0.5 text-sm text-slate-500">{getDonaturDisplay(selectedTransaction)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseDetail}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Tutup detail transaksi"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-5 p-6">
              {/* Nominal + status banner */}
              <div className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-gradient-to-br from-blue-50 to-slate-50 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Nominal Transaksi</p>
                  <p className="mt-2 text-3xl font-extrabold text-slate-900">{formatCurrency(selectedTransaction.jumlah || selectedTransaction.jumlah_donasi || 0)}</p>
                </div>
                <span className={`inline-flex w-fit rounded-xl border px-4 py-1.5 text-sm font-bold ${getStatusBadgeClass(getStatusDisplay(selectedTransaction))}`}>
                  {getStatusDisplay(selectedTransaction)}
                </span>
              </div>

              {/* Detail grid */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailItem label="Jenis Donasi">{getJenisMeta(selectedTransaction).nama}</DetailItem>
                <DetailItem label="Program Donasi">{getDetailDonasiDisplay(selectedTransaction)}</DetailItem>
                <DetailItem label="Tanggal & Jam">
                  {formatDateTime(selectedTransaction.tanggal_transaksi || selectedTransaction.tgl_transaksi || selectedTransaction.created_at || selectedTransaction.tanggal || selectedTransaction.tgl)}
                </DetailItem>
                <DetailItem label="Metode Pembayaran">{getMetodeDisplay(selectedTransaction)}</DetailItem>
                <DetailItem label="Detail Bank">{getDetailHimpunDisplay(selectedTransaction)}</DetailItem>
                <DetailItem label="Kode Donatur">{selectedTransaction.kode_donatur || selectedTransaction.donatur?.kode_donatur || '-'}</DetailItem>
              </div>

              {/* Catatan */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Catatan Donatur</p>
                <p className="mt-2 text-sm leading-6 text-slate-700">
                  {selectedTransaction.catatan || selectedTransaction.catatan_donatur || 'Tidak ada catatan pada transaksi ini.'}
                </p>
              </div>

              {/* Foto bukti */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Foto Bukti Donatur</p>
                  {getProofUrlForTransaction(selectedTransaction) ? (
                    <a href={getProofUrlForTransaction(selectedTransaction)} target="_blank" rel="noreferrer"
                      className="text-xs font-bold text-blue-700 hover:text-blue-900 transition-colors">
                      Buka ukuran penuh ↗
                    </a>
                  ) : null}
                </div>
                {(() => {
                  const proofUrl = getProofUrlForTransaction(selectedTransaction);
                  return proofUrl ? (
                    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                      <img src={proofUrl} alt="Foto bukti donatur" className="max-h-[360px] w-full object-contain" />
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-400">Belum ada foto bukti yang disertakan donatur.</p>
                  );
                })()}
              </div>

              {/* Upload konfirmasi */}
              {(() => {
                const selectedStatus = (selectedTransaction.status || selectedTransaction.status_transaksi || selectedTransaction.statusTransaksi || '').toString().toLowerCase();
                const canConfirmSelected = selectedStatus === 'diproses' || selectedStatus === 'processed' || selectedStatus === 'processing';
                const existingKonfirmasi = findKonfirmasiForTransaction(selectedTransaction);
                const existingFotoKonfirmasi = existingKonfirmasi?.foto_konfirmasi ? buildFileUrl(existingKonfirmasi.foto_konfirmasi) : '';

                return (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Foto Konfirmasi Admin</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          Upload foto konfirmasi terlebih dahulu. Setelah file tersimpan, status transaksi akan diubah menjadi sukses.
                        </p>
                        {existingFotoKonfirmasi ? (
                          <a href={existingFotoKonfirmasi} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-xs font-bold text-blue-700 hover:text-blue-900">
                            Lihat foto konfirmasi tersimpan ↗
                          </a>
                        ) : null}
                      </div>
                      {canConfirmSelected ? (
                        <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-bold text-blue-700 transition hover:bg-blue-100 active:scale-95">
                          <FiUpload className="h-4 w-4" />
                          Pilih Foto
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleConfirmationFileChange}
                            className="hidden"
                          />
                        </label>
                      ) : null}
                    </div>
                    <div className={`mt-4 rounded-xl border border-dashed p-4 ${
                      confirmationFile ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'
                    }`}>
                      {confirmationFile ? (
                        <div className="flex items-center gap-2">
                          <FiUpload className="h-4 w-4 text-blue-600" />
                          <p className="text-sm font-semibold text-blue-800">{confirmationFile.name}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">
                          {canConfirmSelected ? 'Belum ada foto konfirmasi yang dipilih.' : 'Transaksi ini tidak menunggu konfirmasi admin.'}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Identitas konfirm */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Identitas User Pengonfirm</p>
                <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                  <div className="rounded-lg bg-white border border-slate-100 px-3 py-2">
                    <span className="text-xs text-slate-400 block">Kode</span>
                    <span className="font-semibold text-slate-800">{selectedTransaction.kode_user || '-'}</span>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-100 px-3 py-2">
                    <span className="text-xs text-slate-400 block">Nama</span>
                    <span className="font-semibold text-slate-800">{selectedTransaction.nama_user || selectedTransaction.user?.nama_user || selectedTransaction.user?.nama || '-'}</span>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-100 px-3 py-2">
                    <span className="text-xs text-slate-400 block">Role</span>
                    <span className="font-semibold text-slate-800">{selectedTransaction.role || selectedTransaction.user?.role || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-end pt-1">
                <div className="flex flex-col gap-3 sm:flex-row">
                  {getStatusDisplay(selectedTransaction) === 'Sukses' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handlePreviewReceipt(selectedTransaction)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-95"
                      >
                        <FiEye className="h-4 w-4" />
                        Preview Struk
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendReceipt(selectedTransaction)}
                        disabled={sendingReceiptId === (selectedTransaction.id || selectedTransaction.kode_transaksi || selectedTransaction.kode)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FiSend className="h-4 w-4" />
                        {sendingReceiptId === (selectedTransaction.id || selectedTransaction.kode_transaksi || selectedTransaction.kode) ? 'Mengirim...' : 'Kirim Struk'}
                      </button>
                    </>
                  ) : null}
                  {(() => {
                    const selectedStatus = (selectedTransaction.status || selectedTransaction.status_transaksi || selectedTransaction.statusTransaksi || '').toString().toLowerCase();
                    const canConfirmSelected = selectedStatus === 'diproses' || selectedStatus === 'processed' || selectedStatus === 'processing';
                    const selectedRowId = selectedTransaction.id || selectedTransaction.kode_transaksi || selectedTransaction.kode;

                    return canConfirmSelected ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenRejectModal(selectedTransaction)}
                          disabled={(updating || savingKonfirmasi) && activeId === selectedRowId}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-bold text-red-700 shadow-sm transition hover:bg-red-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FiXCircle className="h-4 w-4" />
                          {(updating || savingKonfirmasi) && activeId === selectedRowId ? 'Menyimpan...' : 'Tolak Transaksi'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConfirm(selectedTransaction)}
                          disabled={(updating || savingKonfirmasi) && activeId === selectedRowId}
                          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-900 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-900/30 transition hover:bg-blue-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FiCheckCircle className="h-4 w-4" />
                          {(updating || savingKonfirmasi) && activeId === selectedRowId ? 'Menyimpan...' : 'Konfirmasi Sukses'}
                        </button>
                      </>
                    ) : null;
                  })()}
                  <button
                    type="button"
                    onClick={handleCloseDetail}
                    className="rounded-xl border border-slate-200 bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 active:scale-95"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL TOLAK ── */}
      {rejectCandidate && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={handleCloseRejectModal}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1.5 bg-gradient-to-r from-red-700 to-red-400" />
            <div className="p-6">
              <div className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-700">
                  <FiXCircle className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-lg font-bold text-slate-900">Tolak Transaksi?</p>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    Tindakan ini tidak dapat dibatalkan. Status transaksi akan berubah menjadi <span className="font-semibold text-red-600">Ditolak</span>.
                  </p>
                  <div className="mt-3 rounded-xl bg-red-50 border border-red-100 px-4 py-2.5">
                    <p className="text-sm font-bold text-red-800 font-mono">{rejectCandidate.kode_transaksi || rejectCandidate.id || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCloseRejectModal}
                  disabled={updating || savingKonfirmasi}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReject}
                  disabled={updating || savingKonfirmasi}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-700 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-red-700/30 transition hover:bg-red-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiXCircle className="h-4 w-4" />
                  {updating || savingKonfirmasi ? 'Menolak...' : 'Ya, Tolak'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KonfirmasiDonasiAdmin;
